import { FormEvent, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createWebhook,
  deleteWebhook,
  getDeliveryHealth,
  listDeferredWebhooks,
  listSandboxGoals,
  listWebhookDeliveries,
  listWebhooks,
  patchWebhook,
  resumeWebhook,
  retryWebhookDelivery,
  rotateWebhookSecret,
  testWebhook,
  type DeliveryHealth,
  type DestHealthStatus,
  type DestKind,
  type PortalWebhook,
  type WebhookDelivery,
  type WebhookTestResult,
} from '../api/portal';
import { ApiError } from '../api/client';

const KINDS: { id: DestKind; label: string; hint: string; placeholder: string }[] = [
  { id: 'http', label: 'HTTP JSON', hint: 'HMAC POST to your URL. Empty filter = every type.', placeholder: 'https://example.com/hooks/dp' },
  { id: 'slack', label: 'Slack', hint: 'Incoming webhook. Same envelope as text + Play clip.', placeholder: 'https://hooks.slack.com/services/…' },
  { id: 'telegram', label: 'Telegram', hint: 'telegram:CHAT_ID + bot token in config.', placeholder: 'telegram:-1001234567890' },
  { id: 'instagram', label: 'Instagram', hint: 'instagram:HANDLE + Meta token, or https webhook (Make/Zapier).', placeholder: 'instagram:arkusliga' },
  { id: 'facebook', label: 'Facebook', hint: 'facebook:PAGE_ID + token, or https webhook.', placeholder: 'facebook:123456789' },
  { id: 'whatsapp', label: 'WhatsApp', hint: 'whatsapp:+3816… Cloud API token, or wa.me share link.', placeholder: 'whatsapp:+38164111222' },
  { id: 'email', label: 'Email', hint: 'mailto: plus SMTP_URL on the dispatcher or config.smtp_url.', placeholder: 'mailto:desk@club.rs' },
  { id: 'folder', label: 'Local / NAS folder', hint: 'We copy the MP4 onto a path the dispatcher can write.', placeholder: 'folder:D:/drops/alerts' },
  { id: 's3', label: 'S3 / MinIO', hint: 'We PUT the MP4. Needs access_key + secret_key.', placeholder: 's3://bucket/alerts' },
  { id: 'ftp', label: 'FTP', hint: 'We STOR the MP4. User/pass in the URL or config.', placeholder: 'ftp://user:pass@host/inbox' },
  { id: 'dropbox', label: 'Dropbox', hint: 'We upload the MP4. Needs dropbox_token.', placeholder: 'dropbox://Highlights' },
];

export function WebhooksPage() {
  const qc = useQueryClient();
  const hooks = useQuery({ queryKey: ['portal-webhooks'], queryFn: listWebhooks });
  const inbox = useQuery({ queryKey: ['portal-deliveries'], queryFn: listWebhookDeliveries, refetchInterval: 8000 });
  const deferred = useQuery({ queryKey: ['portal-deferred'], queryFn: listDeferredWebhooks, refetchInterval: 15_000 });
  const [editId, setEditId] = useState<string | null>(null);
  const [kind, setKind] = useState<DestKind>('http');
  const [openDelivery, setOpenDelivery] = useState<WebhookDelivery | null>(null);
  const [url, setUrl] = useState('');
  const [eventFilter, setEventFilter] = useState('');
  const [mediaOnly, setMediaOnly] = useState(false);
  const [token, setToken] = useState('');
  const [accessKey, setAccessKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [region, setRegion] = useState('us-east-1');
  const [secret, setSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [testById, setTestById] = useState<Record<string, WebhookTestResult | 'pending'>>({});
  const [replayGoal, setReplayGoal] = useState('');
  const goals = useQuery({ queryKey: ['portal-sandbox-goals'], queryFn: () => listSandboxGoals() });

  const meta = useMemo(() => KINDS.find((k) => k.id === kind) ?? KINDS[0], [kind]);

  const createMut = useMutation({
    mutationFn: () => {
      const config: Record<string, string> = {};
      if (kind === 'telegram' && token.trim()) config.bot_token = token.trim();
      if ((kind === 'instagram' || kind === 'facebook' || kind === 'whatsapp') && token.trim()) {
        config.access_token = token.trim();
      }
      if (kind === 'dropbox' && token.trim()) config.dropbox_token = token.trim();
      if (kind === 'email' && token.trim()) config.smtp_url = token.trim();
      if (kind === 's3') {
        if (accessKey.trim()) config.access_key = accessKey.trim();
        if (secretKey.trim()) config.secret_key = secretKey.trim();
        if (region.trim()) config.region = region.trim();
      }
      return createWebhook({
        url,
        event_filter: eventFilter.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean),
        kind,
        media_only: mediaOnly,
        config,
      });
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['portal-webhooks'] });
      setSecret(data.secret);
      setUrl('');
      setEventFilter('');
      setToken('');
      setAccessKey('');
      setSecretKey('');
      setError(null);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });
  const delMut = useMutation({
    mutationFn: deleteWebhook,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portal-webhooks'] }),
  });
  const rotateMut = useMutation({
    mutationFn: rotateWebhookSecret,
    onSuccess: (data) => {
      setSecret(data.secret);
      qc.invalidateQueries({ queryKey: ['portal-webhooks'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });
  const testMut = useMutation({
    mutationFn: ({ id, mode, event_id }: { id: string; mode?: string; event_id?: string }) =>
      testWebhook(id, mode || event_id ? { mode, event_id } : undefined),
    onMutate: (vars) => setTestById((prev) => ({ ...prev, [vars.id]: 'pending' })),
    onSuccess: (res, vars) => {
      setTestById((prev) => ({ ...prev, [vars.id]: res }));
      qc.invalidateQueries({ queryKey: ['portal-deliveries'] });
    },
    onError: (e, vars) => setTestById((prev) => ({
      ...prev,
      [vars.id]: { ok: false, error: e instanceof ApiError ? e.message : String(e) },
    })),
  });
  const retryMut = useMutation({
    mutationFn: retryWebhookDelivery,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portal-deliveries'] }),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    createMut.mutate();
  };

  return (
    <div>
      <div className="page-head">
        <div className="page-kicker">Delivery</div>
        <h1 className="page-title">Webhooks</h1>
        <p className="page-sub">
          Edit the destination, rotate the HMAC secret, see rows waiting for the morning pack.
          Test is a sample goal through the dispatcher — a ping is never shown as green.
        </p>
      </div>
      <form onSubmit={onSubmit} className="card p-4 mb-4 grid gap-3">
        <div className="flex gap-2 flex-wrap items-end">
          <div className="w-56">
            <label className="label">Destination</label>
            <select className="input" value={kind} onChange={(e) => setKind(e.target.value as DestKind)}>
              {KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-[16rem]">
            <label className="label">URL / path</label>
            <input className="input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder={meta.placeholder} required />
          </div>
          <div className="w-64">
            <label className="label">Event types (optional)</label>
            <input className="input" value={eventFilter} onChange={(e) => setEventFilter(e.target.value)} placeholder="goal, card, reel.ready" />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-300 pb-2">
            <input type="checkbox" checked={mediaOnly} onChange={(e) => setMediaOnly(e.target.checked)} />
            Hold for media
          </label>
          <button className="btn-primary" disabled={createMut.isPending}>Add</button>
        </div>
        <p className="text-xs text-slate-500 m-0">{meta.hint} Secrets in config are stored but never listed again.</p>
        {kind === 'telegram' && (
          <div className="w-full max-w-lg">
            <label className="label">Bot token</label>
            <input className="input" type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="123456:AA…" />
          </div>
        )}
        {(kind === 'instagram' || kind === 'facebook' || kind === 'whatsapp') && (
          <div className="w-full max-w-lg">
            <label className="label">{kind === 'whatsapp' ? 'WhatsApp / Meta token' : 'Meta access token'}</label>
            <input className="input" type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="EAAG…" />
          </div>
        )}
        {kind === 'dropbox' && (
          <div className="w-full max-w-lg">
            <label className="label">Dropbox token</label>
            <input className="input" type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="sl.…" />
          </div>
        )}
        {kind === 'email' && (
          <div className="w-full max-w-lg">
            <label className="label">SMTP URL (optional if dispatcher has SMTP_URL)</label>
            <input className="input" value={token} onChange={(e) => setToken(e.target.value)} placeholder="smtp://user:pass@host:587" />
          </div>
        )}
        {kind === 's3' && (
          <div className="flex gap-2 flex-wrap">
            <div className="w-48">
              <label className="label">Access key</label>
              <input className="input" value={accessKey} onChange={(e) => setAccessKey(e.target.value)} />
            </div>
            <div className="w-48">
              <label className="label">Secret key</label>
              <input className="input" type="password" value={secretKey} onChange={(e) => setSecretKey(e.target.value)} />
            </div>
            <div className="w-36">
              <label className="label">Region</label>
              <input className="input" value={region} onChange={(e) => setRegion(e.target.value)} />
            </div>
          </div>
        )}
      </form>
      {error && <div className="text-bad text-sm mb-3">{error}</div>}
      {secret && (
        <div className="card p-3 mb-4 text-sm text-warn">
          Signing secret (shown once, HTTP HMAC): <code className="break-all">{secret}</code>
        </div>
      )}
      {(goals.data ?? []).length > 0 && (
        <div className="mb-3 flex flex-wrap items-end gap-2">
          <div className="min-w-[18rem] flex-1">
            <label className="label">Replay archive goal</label>
            <select className="input" value={replayGoal} onChange={(e) => setReplayGoal(e.target.value)}>
              <option value="">Latest sample</option>
              {goals.data!.map((g) => (
                <option key={g.id} value={g.id}>{g.label}</option>
              ))}
            </select>
          </div>
        </div>
      )}
      <DeliveryHealthCard />
      <div className="card p-4 mb-4">
        <table className="table">
          <thead>
            <tr><th>Dest</th><th>URL</th><th>Events</th><th>Media</th><th>Test</th><th></th></tr>
          </thead>
          <tbody>
            {(hooks.data ?? []).map((h) => (
              <tr key={h.id}>
                <td className="text-xs uppercase text-slate-400">{h.kind ?? 'http'}</td>
                <td className="break-all">{h.url}</td>
                <td className="text-xs text-slate-400">
                  {(h.event_filter ?? []).length ? h.event_filter!.join(', ') : 'all types'}
                </td>
                <td className="text-xs">{h.media_only ? 'clip only' : 'all'}</td>
                <td>
                  {h.is_active && (
                    <button
                      type="button"
                      className="btn-secondary text-xs"
                      disabled={testMut.isPending}
                      onClick={() => testMut.mutate(replayGoal
                        ? { id: h.id, mode: 'replay', event_id: replayGoal }
                        : { id: h.id })}
                    >
                      {testById[h.id] === 'pending' ? 'Sending…' : 'Test'}
                    </button>
                  )}
                  <WebhookTestLabel result={testById[h.id]} />
                </td>
                <td className="flex flex-wrap gap-1">
                  <button type="button" className="btn-ghost text-xs" onClick={() => setEditId(editId === h.id ? null : h.id)}>Edit</button>
                  <button type="button" className="btn-ghost text-xs" disabled={rotateMut.isPending} onClick={() => rotateMut.mutate(h.id)}>Rotate secret</button>
                  <button className="btn-danger text-xs" onClick={() => delMut.mutate(h.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {editId && (hooks.data ?? []).some((h) => h.id === editId) && (
          <WebhookEdit
            hook={(hooks.data ?? []).find((h) => h.id === editId)!}
            onClose={() => setEditId(null)}
          />
        )}
      </div>

      {(deferred.data ?? []).length > 0 && (
        <div className="card p-4 mb-4">
          <div className="mb-2 text-[11px] uppercase tracking-wide text-slate-500">Waiting morning pack</div>
          <table className="table">
            <thead>
              <tr><th>Release</th><th>Type</th><th>Dest</th><th>Reason</th></tr>
            </thead>
            <tbody>
              {deferred.data!.map((d) => (
                <tr key={d.id}>
                  <td className="text-xs whitespace-nowrap">{d.release_at.replace('T', ' ').slice(0, 16)}</td>
                  <td className="text-xs">{d.event_type}</td>
                  <td className="text-xs break-all">{d.kind} · {d.url}</td>
                  <td className="text-xs text-amber-200">{d.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="page-head mt-8">
        <div className="page-kicker">Inbox</div>
        <h2 className="page-title text-xl">Deliveries</h2>
        <p className="page-sub">One row per attempt. Same <code>delivery_id</code> with revision 1 then 2 = title then overlay. Retry re-sends that payload.</p>
      </div>
      <div className="card p-4">
        <table className="table">
          <thead>
            <tr><th>When</th><th>Type</th><th>Rev</th><th>Dest</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {(inbox.data ?? []).map((d) => (
              <tr
                key={d.id}
                className="cursor-pointer hover:bg-white/[0.04]"
                onClick={() => setOpenDelivery(d)}
              >
                <td className="text-xs whitespace-nowrap">{d.created_at.replace('T', ' ').slice(0, 19)}</td>
                <td className="text-xs">{d.event_type}</td>
                <td className="text-xs">{d.revision}</td>
                <td className="text-xs break-all">{d.kind} · {d.url}</td>
                <td className={`text-xs ${d.status === 'ok' ? 'text-emerald-400' : 'text-red-400'}`}>
                  {d.status}{d.http_status != null ? ` ${d.http_status}` : ''}
                  {d.error ? ` · ${d.error}` : ''}
                  {d.attempt > 1 ? ` · try ${d.attempt}` : ''}
                </td>
                <td>
                  <button
                    type="button"
                    className="btn-secondary text-xs"
                    disabled={retryMut.isPending}
                    onClick={(e) => { e.stopPropagation(); retryMut.mutate(d.id); }}
                  >
                    Retry
                  </button>
                </td>
              </tr>
            ))}
            {(inbox.data ?? []).length === 0 && (
              <tr><td colSpan={6} className="text-sm text-slate-500">No deliveries yet. Add a hook and click Test, or Save a goal on the desk.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {openDelivery && (
        <DeliveryDrawer
          row={openDelivery}
          onClose={() => setOpenDelivery(null)}
        />
      )}
    </div>
  );
}

const STATUS_TONE: Record<DestHealthStatus, string> = {
  healthy: 'text-emerald-400',
  degraded: 'text-amber-300',
  failing: 'text-red-400',
  paused: 'text-red-400',
  disabled: 'text-slate-500',
  idle: 'text-slate-500',
};

function fmtMs(ms: number | null | undefined): string {
  if (ms == null) return '—';
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(ms < 10_000 ? 1 : 0)} s`;
}

function fmtWhen(ts: string | null | undefined): string {
  return ts ? ts.replace('T', ' ').slice(0, 16) : '—';
}

function DeliveryHealthCard() {
  const qc = useQueryClient();
  const health = useQuery({ queryKey: ['portal-delivery-health'], queryFn: getDeliveryHealth, refetchInterval: 30_000 });
  const resume = useMutation({
    mutationFn: resumeWebhook,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal-delivery-health'] });
      qc.invalidateQueries({ queryKey: ['portal-webhooks'] });
    },
  });
  const data: DeliveryHealth | undefined = health.data;
  if (!data || data.destinations.length === 0) return null;
  const paused = data.destinations.filter((d) => d.status === 'paused');
  return (
    <div className="card p-4 mb-4">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <div className="text-[11px] uppercase tracking-wide text-slate-500">Delivery health · last 24h</div>
        <div className="text-xs text-slate-400">
          Origo ingest p50 {fmtMs(data.ingest.latency_p50_ms)} · p95 {fmtMs(data.ingest.latency_p95_ms)}
          {' '}({data.ingest.events_24h} events)
        </div>
      </div>
      {paused.length > 0 && (
        <div className="mb-3 rounded-lg bg-red-500/10 p-3 text-sm text-red-200">
          {paused.length === 1 ? 'A destination was' : `${paused.length} destinations were`} paused after failing continuously.
          Fix the endpoint and press Resume. Missed rows can be re-sent from the inbox below.
        </div>
      )}
      <table className="table">
        <thead>
          <tr><th>Dest</th><th>Status</th><th>Success</th><th>Event → you p50 / p95</th><th>Last OK</th><th>Last error</th><th></th></tr>
        </thead>
        <tbody>
          {data.destinations.map((d) => (
            <tr key={d.id}>
              <td className="text-xs break-all"><span className="uppercase text-slate-400">{d.kind}</span> · {d.url}</td>
              <td className={`text-xs font-semibold ${STATUS_TONE[d.status]}`}>{d.status}</td>
              <td className="text-xs">
                {d.success_rate == null ? '—' : `${Math.round(d.success_rate * 1000) / 10}%`}
                <span className="text-slate-500"> ({d.ok_24h}/{d.attempts_24h})</span>
              </td>
              <td className="text-xs">{fmtMs(d.latency_p50_ms)} / {fmtMs(d.latency_p95_ms)}</td>
              <td className="text-xs whitespace-nowrap">{fmtWhen(d.last_ok_at)}</td>
              <td className="text-xs text-red-300 break-all">
                {d.status === 'paused' && d.paused_reason ? d.paused_reason : d.last_error ?? '—'}
              </td>
              <td>
                {(d.status === 'paused' || d.status === 'disabled') && (
                  <button
                    type="button"
                    className="btn-primary text-xs"
                    disabled={resume.isPending}
                    onClick={() => resume.mutate(d.id)}
                  >
                    Resume
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function WebhookEdit({ hook, onClose }: { hook: PortalWebhook; onClose: () => void }) {
  const qc = useQueryClient();
  const [url, setUrl] = useState(hook.url);
  const [eventFilter, setEventFilter] = useState((hook.event_filter ?? []).join(', '));
  const [mediaOnly, setMediaOnly] = useState(!!hook.media_only);
  const [active, setActive] = useState(hook.is_active);
  const [err, setErr] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => patchWebhook(hook.id, {
      url,
      event_filter: eventFilter.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean),
      media_only: mediaOnly,
      is_active: active,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal-webhooks'] });
      onClose();
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : String(e)),
  });
  return (
    <form
      className="mt-4 grid gap-2 rounded-lg bg-black/30 p-3"
      onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
    >
      <div className="text-xs uppercase tracking-wide text-slate-500">Edit destination</div>
      <input className="input" value={url} onChange={(e) => setUrl(e.target.value)} />
      <input className="input" value={eventFilter} onChange={(e) => setEventFilter(e.target.value)} placeholder="goal, reel.ready" />
      <label className="flex items-center gap-2 text-sm text-slate-300">
        <input type="checkbox" checked={mediaOnly} onChange={(e) => setMediaOnly(e.target.checked)} />
        Hold for media
      </label>
      <label className="flex items-center gap-2 text-sm text-slate-300">
        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
        Active
      </label>
      {err && <div className="text-sm text-rose-300">{err}</div>}
      <div className="flex gap-2">
        <button type="submit" className="btn-primary text-xs" disabled={save.isPending}>Save</button>
        <button type="button" className="btn-ghost text-xs" onClick={onClose}>Cancel</button>
      </div>
    </form>
  );
}

function DeliveryDrawer({ row, onClose }: { row: WebhookDelivery; onClose: () => void }) {
  const raw = JSON.stringify(row.payload ?? { note: 'no payload stored' }, null, 2);
  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/50" onClick={onClose}>
      <aside
        className="flex h-full w-full max-w-lg flex-col bg-surface shadow-xl ring-1 ring-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-white/5 px-4 py-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">Delivery body</div>
            <div className="font-semibold text-white">{row.event_type} · rev {row.revision}</div>
          </div>
          <button type="button" className="btn-ghost text-xs" onClick={onClose}>Close</button>
        </div>
        <pre className="min-h-0 flex-1 overflow-auto px-4 py-3 text-[11px] text-slate-300">{raw}</pre>
        <div className="border-t border-white/5 px-4 py-3">
          <button
            type="button"
            className="btn-primary text-xs"
            onClick={() => { void navigator.clipboard.writeText(raw); }}
          >
            Copy JSON
          </button>
        </div>
      </aside>
    </div>
  );
}

function WebhookTestLabel({ result }: { result?: WebhookTestResult | 'pending' }) {
  if (!result || result === 'pending') return null;
  const extra = result.payload && typeof result.payload === 'object' && 'payload' in (result.payload as object)
    ? (result.payload as { payload?: { player?: { name?: string }; clip_url?: string } }).payload
    : undefined;
  return (
    <div className={`text-[11px] mt-1 ${result.ok ? 'text-emerald-400' : 'text-red-400'}`}>
      {result.ok
        ? `OK ${result.status ?? ''} · ${result.duration_ms ?? 0}ms${extra?.player?.name ? ` · ${extra.player.name}` : ''}${extra?.clip_url ? ' · clip' : ''}`
        : (result.error ?? `HTTP ${result.status ?? '?'}`)}
      {result.fallback === 'ping' ? ' · dispatcher down — not a delivery test' : ''}
    </div>
  );
}
